import { useMemo } from "react";
import { useNetsuiteAccounts, useNetsuiteSubsidiaries } from "@api/hooks";

/**
 * Resolves the NetSuite account/subsidiary ids referenced by a JE-style
 * payload's line items into display names, for JE preview summary grids
 * (DepositToSafePreview, ReverseJournalEntryPreview, etc).
 *
 * payload shape: { subsidiary: { id }, line: { items: [{ account: { id }, lineSubsidiary: { id } }] } }
 *
 * Returns { accountNameById, subsidiaryNameById, accountsQuery, subsidiariesQuery }.
 */
export default function useJournalEntryLineLookups(payload) {
  const items = useMemo(() => payload?.line?.items ?? [], [payload]);

  const accountIds = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const it of items) {
      const id = it?.account?.id;
      if (id === null || id === undefined || id === "") continue;
      const key = String(id);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(key);
    }
    return out;
  }, [items]);

  const subsidiaryIds = useMemo(() => {
    const seen = new Set();
    const out = [];
    const headerSid = payload?.subsidiary?.id;
    if (headerSid !== undefined && headerSid !== null && headerSid !== "") {
      seen.add(String(headerSid));
      out.push(String(headerSid));
    }
    for (const it of items) {
      const id = it?.lineSubsidiary?.id;
      if (id === null || id === undefined || id === "") continue;
      const key = String(id);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(key);
    }
    return out;
  }, [items, payload]);

  const accountsQuery = useNetsuiteAccounts(
    { ids: accountIds },
    { enabled: accountIds.length > 0 },
  );
  const subsidiariesQuery = useNetsuiteSubsidiaries(
    { ids: subsidiaryIds },
    { enabled: subsidiaryIds.length > 0 },
  );

  const accountNameById = useMemo(() => {
    const data = accountsQuery.data;
    const list = data?.items ?? data?.results ?? (Array.isArray(data) ? data : []);
    const map = {};
    for (const a of list) {
      const id = a?.id ?? a?.ID;
      const name = a?.displaynamewithhierarchy ?? a?.displayNameWithHierarchy;
      if (id !== undefined && id !== null) map[String(id)] = name ?? "";
    }
    return map;
  }, [accountsQuery.data]);

  const subsidiaryNameById = useMemo(() => {
    const data = subsidiariesQuery.data;
    const list = data?.items ?? data?.results ?? (Array.isArray(data) ? data : []);
    const map = {};
    for (const s of list) {
      const id = s?.id ?? s?.ID;
      const name = s?.name ?? s?.fullname ?? s?.fullName;
      if (id !== undefined && id !== null) map[String(id)] = name ?? "";
    }
    return map;
  }, [subsidiariesQuery.data]);

  return { accountNameById, subsidiaryNameById, accountsQuery, subsidiariesQuery };
}

/**
 * Plain (no currency symbol) two-decimal number format for JE line-item
 * amounts, e.g. debit/credit table cells.
 */
export function fmtLineAmount(n) {
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Sums a JE payload's line items into { totalDebit, totalCredit, balanced }.
 * `balanced` allows for floating-point rounding noise.
 */
export function computeLineBalance(items) {
  const totalDebit = items.reduce((s, it) => s + (Number(it.debit) || 0), 0);
  const totalCredit = items.reduce((s, it) => s + (Number(it.credit) || 0), 0);
  return { totalDebit, totalCredit, balanced: Math.abs(totalDebit - totalCredit) < 0.01 };
}
